import { Button, FlexBox, Icon, Text } from '@fable/components'
import { OverflowMenu, ProgressCircle } from '../shared'
import { css, useTheme } from '@fable/theme'
import { StatsUserImage } from '../StatsUserImage'
import { useStats } from '../../lib/ctx/stats'
import { analytics } from 'segment_analytics'
import { useState } from 'react'
import { useStatsPermission } from '../../lib/hooks'
import { sendToNative } from 'app/webview/utils'

export const StatsHeroFallback = () => {
  const { colors } = useTheme()
  const [open, setOpen] = useState(false)
  const [theme, stats, year, isProfileView, isDesktop] = useStats((s) => [
    s.theme,
    s.stats,
    s.year,
    s.isProfileView,
    s.isDesktop,
  ])

  const title = `${year} Reading Goal`
  const { isStatsOwner } = useStatsPermission()
  const allowCreateGoal = stats && isStatsOwner && !isDesktop

  const handleSetGoal = () => {
    sendToNative({
      id: 'create-reading-goal',
      payload: {
        type: 'deep_link',
        body: {
          url: `https://fable.co/app/reading_goals_edit?create=${year}`,
          close: true,
        },
      },
    })
  }

  const getTextColor = () => {
    if (theme.key === 'blue' || theme.key === 'green' || theme.key === 'pink') {
      return colors.uiWhite
    }

    return theme.summaryText
  }

  const textColor = getTextColor()

  return (
    <>
      <FlexBox
        className={css`
          position: absolute;
          top: ${isProfileView ? 1 : -16}px;
          left: 50%;
          transform: translateX(-50%);
        `}
        centerAll
      >
        <ProgressCircle
          percentage={0}
          width={isProfileView ? 80 : 166}
          transitionDelay={0.4}
          strokeWidth={isProfileView ? 20 : 14}
          backgroundColor={colors.uiBlack40}
          cutout
        />
      </FlexBox>
      {isProfileView ? (
        <FlexBox
          className={css`
            width: 40px;
            height: 40px;
            background-color: ${colors.uiWhite};
            border-radius: 50%;
          `}
          centerAll
        >
          <Icon
            type="trophy"
            className={css`
              width: 20px;
            `}
            fill={theme.accent}
          />
        </FlexBox>
      ) : (
        <StatsUserImage />
      )}
      <FlexBox
        className={css`
          padding: 0 20px;
          width: 100%;
          margin-top: 12px;
        `}
        alignItems="center"
        flexDirection="column"
      >
        <Text
          className={css`
            margin-bottom: 12px;
            transition: color 0.6s ease-in-out;
          `}
          type="body"
          sizing="M"
          color={textColor}
          align="center"
          weight="500"
        >
          {title}
        </Text>
        {allowCreateGoal ? (
          <Button
            dataTestId="stats-goal-setup-button"
            className={css`
              max-width: 152px;
              background-color: ${colors.uiBlack10};
              border: none;
              &:hover {
                background-color: ${colors.uiBlack10};
                border: none;
              }
            `}
            onClick={handleSetGoal}
          >
            Set goal
          </Button>
        ) : (
          <OverflowMenu
            open={open}
            setOpen={setOpen}
            align="center"
            disabled={!isStatsOwner}
            trigger={
              <Button
                dataTestId="stats-goal-setup-button"
                className={css`
                  max-width: 152px;
                  background-color: ${colors.uiBlack10};
                  border: none;
                  &:hover {
                    background-color: ${colors.uiBlack10};
                    border: none;
                  }
                `}
                onClick={() => analytics.events.stats.statsGoalSetupModal()}
              >
                Not set
              </Button>
            }
          >
            <FlexBox
              className={css`
                padding: 0 12px;
              `}
              flexDirection="column"
            >
              <Icon
                className={css`
                  margin-bottom: 4px;
                  width: 20px;
                `}
                type="info"
                fill={colors.uiPewterGrey}
              />
              <Text type="body" sizing="M" color={colors.uiWhite}>
                Set up your Reading Goal in Settings to get the most out of our
                Stats feature!
              </Text>
            </FlexBox>
          </OverflowMenu>
        )}
      </FlexBox>
    </>
  )
}
